import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronDownIcon,
  BuildingOfficeIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { useDebounce } from '@hooks/useDebounce';
import { useAuthStore } from '@features/auth/store/authStore';
import { getDefaultRouteForRole } from '@lib/utils/roleNavigation';

const PAGE_SIZE = 50;

/**
 * Identidad visual de la compania activa: su logo sobre una placa blanca
 * cuadrada, o el icono generico si no tiene.
 *
 * Este es el lugar del logo del cliente en el portal, y no la cabecera del
 * sidebar. Dos razones. La superficie de la barra superior ya es CLARA, que es
 * lo que necesitan los logos que suben los clientes (JPG con fondo blanco y
 * tinta oscura, hechos para fondo claro): aca se funden solos, sin ninguna
 * placa de rescate que perfore el sidebar oscuro. Y esta pegado al control que
 * cambia de empresa, que es donde el dato "en que empresa estoy" es util y
 * accionable. Ver `SidebarBrand` para la auditoria completa.
 *
 * La placa es cuadrada y chica a proposito: normaliza logos de cualquier
 * proporcion —uno apaisado y uno vertical ocupan la misma caja— que es lo que
 * hacen Freshdesk, QuickBooks y los avatares de entidad de Atlassian y Geist.
 */
function CompanyAvatar({ src, name, size = 24 }: { src?: string | null; name?: string; size?: number }) {
  if (!src) {
    return <BuildingOfficeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />;
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-inset ring-gray-200"
      style={{ width: size, height: size, padding: 2 }}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="h-full w-full object-contain"
        onError={(e) => {
          // Un logo borrado en Aludra no debe dejar un hueco: se esconde la
          // imagen y queda la placa vacia, que sigue siendo un avatar valido.
          e.currentTarget.style.visibility = 'hidden';
        }}
        data-testid="tenant-switcher-company-logo"
      />
      <span className="sr-only">{name}</span>
    </span>
  );
}

export function TenantSwitcher() {
  const queryClient = useQueryClient();
  const tenantInfo = useAuthStore((s) => s.tenantInfo);
  const user = useAuthStore((s) => s.user);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 300);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get selected company
  const { data: selectedCompanyData } = useQuery({
    queryKey: ['company', 'selected'],
    queryFn: companyService.getSelectedCompany,
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    retry: 2,
  });

  // Get user's companies from session (cached from login) to determine if switcher should show
  // This uses /companies endpoint which returns only the user's assigned companies
  const { data: userCompaniesData } = useQuery({
    queryKey: ['companies'],
    queryFn: companyService.getCompanies,
    staleTime: 5 * 60 * 1000,
  });

  const totalCompanies = userCompaniesData?.count || 0;

  // Infinite scroll query for all companies
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ['companies', 'switcher-infinite', debouncedSearch],
    queryFn: ({ pageParam = 1 }) =>
      companyService.searchCompanies(pageParam, PAGE_SIZE, debouncedSearch || undefined),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 30 * 1000,
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Select company mutation
  const selectCompanyMutation = useMutation({
    mutationFn: (companyId: string) => companyService.selectCompany(companyId),
    onSuccess: async () => {
      // Clear all cached queries and localStorage filters
      queryClient.clear();
      localStorage.removeItem('invoice-filter-storage');

      // Full page load (not reload) to ensure all data is fresh for the new
      // company AND the user lands on a route valid for it. Reloading the
      // current URL could leave the user on a company/record-specific route
      // (e.g. an invoice detail belonging to the previous company), showing
      // stale/foreign tenant data. Mirrors CompanySelectorPage's approach.
      const defaultRoute = getDefaultRouteForRole(user?.role);
      window.location.href = defaultRoute;
    },
  });

  // Flatten all pages into single array
  const companies = data?.pages.flatMap((page) => page.companies) || [];
  const total = data?.pages[0]?.total || 0;
  const selectedCompany = selectedCompanyData?.company || null;
  const isSelecting = selectCompanyMutation.isPending;

  // ¿La compañía seleccionada coincide con la búsqueda actual? (sin búsqueda, siempre).
  const normalizedSearch = debouncedSearch.trim().toLowerCase();
  const selectedMatchesSearch =
    !!selectedCompany &&
    (normalizedSearch === '' ||
      [selectedCompany.name, selectedCompany.ruc, selectedCompany.code].some((field) =>
        field?.toLowerCase().includes(normalizedSearch)
      ));

  // Fija la compañía seleccionada al inicio de la lista (sin duplicarla) para que el
  // usuario la identifique fácilmente, sobre todo con nombres parecidos. Si hay una
  // búsqueda activa, solo se fija cuando la seleccionada coincide con el término.
  const displayCompanies =
    selectedCompany && selectedMatchesSearch
      ? [selectedCompany, ...companies.filter((c) => c.id !== selectedCompany.id)]
      : companies;

  // Handle scroll to load more
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset search when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  // Handle company selection
  const handleSelectCompany = (companyId: string) => {
    selectCompanyMutation.mutate(companyId);
    setIsOpen(false);
  };

  // With only one company (or on a NAOS-branded domain, where el selector queda
  // fijo a la compañía actual — ver NAOS_Analisis_Funcionalidad.md §6), show the
  // name as a read-only badge (no dropdown needed).
  if (totalCompanies <= 1 || tenantInfo?.isNaos) {
    if (!selectedCompany) return null;
    return (
      <div className="inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300" data-testid="tenant-switcher-readonly-badge">
        <CompanyAvatar src={selectedCompany.image || selectedCompany.profileImage} name={selectedCompany.name} />
        {/*
          Una sola línea, siempre — el ancho crece hacia los lados (más vw,
          más px de tope) en vez de pasar a una segunda línea. El cluster
          derecho del topbar es `shrink-0` — ver AppLayout.tsx — así que
          ensanchar esto nunca le saca espacio a nada más, solo se lo saca a
          la miga de pan (que sí puede truncar) si hiciera falta.
        */}
        <span className="max-w-[min(60vw,480px)] truncate whitespace-nowrap" title={selectedCompany.name}>
          {selectedCompany.name}
        </span>
      </div>
    );
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSelecting}
        className="inline-flex w-full items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="tenant-switcher-trigger-button"
      >
        <CompanyAvatar src={selectedCompany?.image || selectedCompany?.profileImage} name={selectedCompany?.name} />
        {/* Ver nota de arriba (badge de solo lectura): una sola línea, crece hacia los lados. */}
        <span
          className="max-w-[min(50vw,400px)] truncate whitespace-nowrap"
          title={selectedCompany?.name || undefined}
        >
          {selectedCompany?.name || 'Seleccionar empresa'}
        </span>
        <ChevronDownIcon className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-96 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" data-testid="tenant-switcher-dropdown">
          <div className="py-1">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Cambiar empresa</p>
                <p className="text-xs text-gray-500 mt-1">
                  {total} {total === 1 ? 'empresa disponible' : 'empresas disponibles'}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                data-testid="tenant-switcher-close-button"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Search Input */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Buscar empresa..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  data-testid="tenant-switcher-search-input"
                />
                {(isFetching && !isFetchingNextPage) && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Companies List with Infinite Scroll */}
            <div
              ref={scrollContainerRef}
              className="max-h-80 overflow-y-auto"
            >
              {isLoading ? (
                <div className="px-4 py-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Cargando empresas...</p>
                </div>
              ) : displayCompanies.length > 0 ? (
                <>
                  {displayCompanies.map((company) => {
                    const isSelected = selectedCompany?.id === company.id;
                    return (
                      <button
                        key={company.id}
                        onClick={() => !isSelected && handleSelectCompany(company.id)}
                        disabled={isSelected || isSelecting}
                        className={`
                          w-full flex items-start gap-x-3 px-4 py-3 text-sm text-gray-900 transition-colors
                          hover:bg-gray-100
                          ${isSelected ? 'bg-primary-50' : ''}
                          disabled:cursor-default
                        `}
                        data-testid={`tenant-switcher-option-${company.id}`}
                      >
                        {/* Company Logo/Icon */}
                        {company.image ? (
                          <img
                            src={company.image}
                            alt={company.name}
                            className="h-10 w-10 rounded-md object-contain bg-white flex-shrink-0"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-md bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
                            <BuildingOfficeIcon className="h-5 w-5 text-white" />
                          </div>
                        )}

                        {/* Company Info */}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="font-medium text-gray-900 truncate">
                            {company.name}
                          </p>
                          {company.ruc && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              RUC: <span className="font-mono">{company.ruc}</span>
                            </p>
                          )}
                          {company.code && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              <span className="font-mono">{company.code}</span>
                            </p>
                          )}
                        </div>

                        {/* Selected Indicator */}
                        {isSelected && (
                          <CheckIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}

                  {/* Loading more indicator */}
                  {isFetchingNextPage && (
                    <div className="px-4 py-3 text-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="text-xs text-gray-500 mt-1">Cargando más...</p>
                    </div>
                  )}

                  {/* Load more button if there are more */}
                  {hasNextPage && !isFetchingNextPage && (
                    <button
                      onClick={() => fetchNextPage()}
                      className="w-full px-4 py-3 text-center text-sm text-primary-600 hover:bg-gray-50 font-medium"
                      data-testid="tenant-switcher-load-more-button"
                    >
                      Cargar más empresas...
                    </button>
                  )}

                  {/* End of list indicator */}
                  {!hasNextPage && companies.length > 0 && (
                    <div className="px-4 py-2 text-center text-xs text-gray-400 border-t border-gray-100">
                      Mostrando {companies.length} de {total} empresas
                    </div>
                  )}
                </>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No se encontraron empresas
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
